import { loadInspectorFindings, inspectorMetadataKey, type InspectorPage } from './inspector-pagination';
import { isTenantSettings, type TenantSettings } from './settings-model';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession, signInWithRedirect, signOut } from 'aws-amplify/auth';
import 'aws-amplify/auth/enable-oauth-listener';

export type RuntimeConfig = {
  region: string; graphqlEndpoint: string; identityUserPoolId: string; identityClientId: string;
  managedLoginDomain: string; redirectSignIn: string[] | string; redirectSignOut: string[] | string;
};
export type Tenant = { tenantId: string; contractId: string; customerId: string; displayName: string; role: string; dataGeneration: number | string; authorizationGeneration?: string };
export type ObservationRow = Record<string, unknown> & { region?: string };
export type ServiceData = { checkedAt?: string | null; lastAttemptAt?: string; status: string; message?: string; error?: string | null; failureKind?: string | null; truncated?: boolean; [key: string]: unknown };
export type Account = {
  accountId: string; name?: string; accountName?: string; regions: string[]; syncStatus: string;
  lastSyncedAt?: string; syncError?: string; errorMessage?: string;
  services?: { name: string; region: string; status: string; message: string; checkedAt: string | null }[];
  health?: ServiceData | null; securityHub?: ServiceData | null; waf?: ServiceData | null; backup?: ServiceData | null; inspector?: ServiceData | null;
};
export type Observations = { tenant: Tenant; accounts: Account[] };

let configPromise: Promise<RuntimeConfig> | undefined;
export function configureAuth(): Promise<RuntimeConfig> {
  return configPromise ??= (async () => {
    const response = await fetch('/runtime-config.json', { cache: 'no-store' });
    if (!response.ok) throw new Error('認証設定を読み込めません。配信環境の設定を確認してください。');
    let config: RuntimeConfig;
    try { config = await response.json(); }
    catch { throw new Error('共通認証の設定ファイルがありません。配信環境の設定を確認してください。'); }
    if (!config.identityUserPoolId || !config.identityClientId || !config.managedLoginDomain || !config.graphqlEndpoint) {
      throw new Error('共通認証または API の設定が不足しています。');
    }
    const urls = (value: string | string[]) => Array.isArray(value) ? value : [value];
    if (!urls(config.redirectSignIn).some(url => new URL(url).origin === location.origin)) {
      throw new Error('現在の URL がログイン先に登録されていません。');
    }
    Amplify.configure({ Auth: { Cognito: {
      userPoolId: config.identityUserPoolId, userPoolClientId: config.identityClientId,
      loginWith: { oauth: {
        domain: config.managedLoginDomain.replace(/^https?:\/\//, '').replace(/\/$/, ''),
        scopes: ['openid', 'email', 'profile'], responseType: 'code',
        redirectSignIn: urls(config.redirectSignIn), redirectSignOut: urls(config.redirectSignOut),
      } },
    } } });
    return config;
  })();
}
export async function sessionEmail(): Promise<string | null> {
  await configureAuth();
  const session = await fetchAuthSession();
  return session.tokens?.idToken ? String(session.tokens.idToken.payload.email ?? 'ログイン済み') : null;
}
export async function login() { await configureAuth(); await signInWithRedirect(); }
export async function logout() { await signOut(); }

async function graphql<T>(query: string, variables: Record<string, unknown>, signal?: AbortSignal): Promise<T> {
  const config = await configureAuth();
  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString();
  if (!idToken) throw new Error('ログインの有効期限が切れました。再度ログインしてください。');
  const response = await fetch(config.graphqlEndpoint, {
    method: 'POST', signal, headers: { 'Content-Type': 'application/json', Authorization: idToken },
    body: JSON.stringify({ query, variables }),
  });
  let body;
  try { body = await response.json(); }
  catch { throw new Error('API の応答を読み取れません。接続先を確認してください。'); }
  if (!response.ok || body.errors?.length) throw new Error('データを取得できません。契約・閲覧権限または API の状態を確認してください。');
  if (!body.data) throw new Error('API の応答にデータがありません。');
  return body.data;
}
export async function listTenants(signal?: AbortSignal): Promise<Tenant[]> {
  const result = await graphql<{ listTenants: Tenant[] }>('query { listTenants { tenantId contractId customerId displayName role dataGeneration authorizationGeneration } }', {}, signal);
  return result.listTenants;
}
export async function getWafEvidence(tenantId: string, accountId: string, aclArn: string, startTime: string, endTime: string, cursor?: string, signal?: AbortSignal, mode: 'dashboard' | 'logs' = 'logs'): Promise<Observations> {
  const result = await graphql<{ getObservations: string | Observations }>('query WafEvidence($tenantId: ID!, $accountId: ID!, $aclArn: String!, $start: AWSDateTime!, $end: AWSDateTime!, $cursor: String, $mode: String) { getObservations(tenantId: $tenantId, accountId: $accountId, wafAclArn: $aclArn, wafStart: $start, wafEnd: $end, wafCursor: $cursor, wafMode: $mode) }', { tenantId, accountId, aclArn, start: startTime, end: endTime, cursor, mode }, signal);
  const payload = typeof result.getObservations === 'string' ? JSON.parse(result.getObservations) : result.getObservations;
  if (payload?.tenant?.tenantId !== tenantId || !Array.isArray(payload.accounts) || payload.accounts.some((account: Account) => account.accountId !== accountId)) throw new Error('WAF の閲覧範囲を確認できません。');
  return payload;
}

export async function getObservations(tenantId: string, signal?: AbortSignal): Promise<Observations> {
  const result = await graphql<{ getObservations: string | Observations }>('query Observations($tenantId: ID!) { getObservations(tenantId: $tenantId) }', { tenantId }, signal);
  const payload = typeof result.getObservations === 'string' ? JSON.parse(result.getObservations) : result.getObservations;
  if (payload?.tenant?.tenantId !== tenantId || !Array.isArray(payload.accounts)) throw new Error('テナント情報の整合性を確認できません。');
  return payload;
}

export async function getTenantSettings(tenantId: string, signal?: AbortSignal): Promise<TenantSettings> {
  const result = await graphql<{ getTenantSettings: string | TenantSettings }>('query TenantSettings($tenantId: ID!) { getTenantSettings(tenantId: $tenantId) }', { tenantId }, signal);
  const payload = typeof result.getTenantSettings === 'string' ? JSON.parse(result.getTenantSettings) : result.getTenantSettings;
  if (!isTenantSettings(payload) || payload.tenant.tenantId !== tenantId) throw new Error('設定の閲覧範囲を確認できません。');
  return payload;
}

export async function getObservationsWithInspector(tenantId: string, signal?: AbortSignal): Promise<Observations> {
  for (let attempt = 0; ; attempt++) {
    signal?.throwIfAborted();
    try {
      const base = await getObservations(tenantId, signal);
      const complete = await loadInspectorFindings(base, async cursor => {
        const result = await graphql<{getInspectorFindings: string | InspectorPage}>('query InspectorFindings($tenantId: ID!, $cursor: String) { getInspectorFindings(tenantId: $tenantId, cursor: $cursor) }', {tenantId, cursor}, signal);
        return typeof result.getInspectorFindings === 'string' ? JSON.parse(result.getInspectorFindings) : result.getInspectorFindings;
      }, signal);
      const latest = await getObservations(tenantId, signal);
      if (inspectorMetadataKey(base) !== inspectorMetadataKey(latest)) throw new Error('Inspectorの収集データが更新されました。表示を更新してください。');
      return complete;
    } catch (error) { if (signal?.aborted || attempt >= 1) throw error; }
  }
}
